// Copyright (c) Docugami, Inc. All rights reserved.

import { INavLink, INavLinkGroup } from "@fluentui/react";
import { useMemo, useState } from "react";
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";
import { FeatureFlags } from "store/featureFlags/types";
import { selectFirstArrivalState } from "store/organization/organizationSlice";
import { AutomationId } from "utils/Automation";
import { useFeatureFlag } from "./FeatureFlags";
import { useDeepLinkInitializer } from "./deepLinkInitializer";
import { NavigationType, useNavigationService } from "./navigationService";
import { useAppSelector } from "./redux";

export enum RouteNavItem {
    Home = 'home',
    MyContent = 'my_content',
    UnderstandDocs = 'understand_docs',
    CreateDocs = 'create_docs',
    AutomatedWorkflows = 'automated_workflows',
    Chat = 'Chat',
    Queries = 'Queries'
}

interface NavItemProps {
    key: string,
    name: string,
    iconName: string,
    onClick: () => void,
    automationId: string,
    isDisabled?: boolean,
    tooltip?: string
}

interface IGlobalNavigation {
    isExpanded: boolean;
    items: INavLinkGroup[];
}

export function useGlobalNavigationItems(): IGlobalNavigation {
    const [isExpanded, setIsExpanded] = useState(false);

    const isExpired = useAppSelector(state => selectIsLimitedOrganization(state));

    const navService = useNavigationService();
    const chatEnabled = useFeatureFlag(FeatureFlags.ChatFeature);

    useDeepLinkInitializer();

    const contentNavDisabled = useAppSelector(selectFirstArrivalState);

    return useMemo(() => {
        function createNavItem({ key, name, iconName, onClick, automationId, isDisabled, tooltip }: NavItemProps): INavLink {
            return {
                key: key,
                name: name,
                iconProps: {
                    iconName: iconName,
                    styles: { root: { fontSize: '25px' } }
                },
                url: '',
                forceAnchor: true,
                onClick: onClick,
                "data-automation-id": automationId,
                disabled: isDisabled,
                title: tooltip
            }
        }

        const disabledTooltip = contentNavDisabled ? `Please add more docs to use these features` : undefined;

        const globalNavGroup = {
            links: [
                createNavItem({
                    key: RouteNavItem.Home,
                    name: 'Home',
                    iconName: 'Home',
                    onClick: () => navService.navToHomePage(NavigationType.PushNewPage),
                    automationId: AutomationId.NavItemHome,
                    isDisabled: contentNavDisabled,
                    tooltip: disabledTooltip
                }),
                createNavItem({
                    key: RouteNavItem.MyContent,
                    name: 'My Content',
                    iconName: 'FolderHorizontal',
                    onClick: () => navService.navToLocalDocs(NavigationType.PushNewPage),
                    automationId: AutomationId.NavItemMyContent,
                    isDisabled: contentNavDisabled,
                    tooltip: disabledTooltip
                })
            ]
        };

        const pageNavGroup = {
            links: [
                createNavItem({
                    key: RouteNavItem.UnderstandDocs,
                    name: 'Reports',
                    iconName: 'Lightbulb',
                    onClick: () => navService.navToUnderstandDocsLandingPage(NavigationType.PushNewPage),
                    automationId: AutomationId.NavItemUnderstandDocs,
                    isDisabled: contentNavDisabled,
                    tooltip: disabledTooltip
                }),
                createNavItem({
                    key: RouteNavItem.CreateDocs,
                    name: 'Authoring',
                    iconName: 'PageEdit',
                    onClick: () => navService.navToCreateDocsLandingPage(NavigationType.PushNewPage),
                    automationId: AutomationId.NavItemCreateDocs,
                    isDisabled: contentNavDisabled,
                    tooltip: disabledTooltip
                }),
                createNavItem({
                    key: RouteNavItem.AutomatedWorkflows,
                    name: 'Automated Workflows',
                    iconName: 'Robot',
                    onClick: () => navService.navToWorkFlowLandingPage(NavigationType.PushNewPage),
                    automationId: AutomationId.NavItemAutomatedWorkflows,
                    isDisabled: contentNavDisabled,
                    tooltip: disabledTooltip
                })
            ]
        };

        if (chatEnabled) {
            pageNavGroup.links.push(
                createNavItem({
                    key: RouteNavItem.Chat,
                    name: 'Chat',
                    iconName: 'Chat',
                    onClick: () => navService.navToChatPage(NavigationType.PushNewPage),
                    automationId: '',
                    isDisabled: contentNavDisabled || isExpired,
                    tooltip: ''
                })
            );
        }

        const toolsGroup = {
            links: [
                createNavItem({
                    key: 'help',
                    name: 'Help',
                    iconName: 'Help',
                    onClick: () => {
                        window.open('https://help.docugami.com', '_blank', 'noreferrer');
                    },
                    automationId: AutomationId.NavItemHelp
                }),
                createNavItem({
                    key: 'feedback',
                    name: 'Contact Support',
                    iconName: 'Feedback',
                    onClick: () => {
                        const userback = (window as any).Userback;
                        if (!!userback) {
                            userback.open();
                        }
                    },
                    automationId: AutomationId.NavItemFeedback
                }),
                {
                    name: '',
                    iconProps: {
                        iconName: !isExpanded ? 'DoubleChevronRight' : 'DoubleChevronLeft',
                        styles: { root: { fontSize: '18px', paddingLeft: '3px' } }
                    },
                    url: '',
                    key: 'expand_menu',
                    onClick: () => setIsExpanded(!isExpanded)
                }
            ]
        };

        return {
            isExpanded: isExpanded,
            items: [globalNavGroup, pageNavGroup, toolsGroup]
        };
    }, [isExpired, isExpanded, contentNavDisabled, chatEnabled, navService])
}
